import { useRouteError, useNavigate } from 'react-router-dom'
import { Button } from '@heroui/react'
import {
  ExclamationTriangleIcon,
  HomeIcon,
  ArrowPathIcon,
} from '@heroicons/react/24/outline'

interface RouteError {
  status?: number
  statusText?: string
  message?: string
  data?: unknown
}

export const RouteErrorPage = ({
  showTechnicalDetails = false,
}: {
  showTechnicalDetails?: boolean
}) => {
  const error = useRouteError() as RouteError
  const navigate = useNavigate()

  const handleGoHome = () => {
    navigate('/', { replace: true })
  }

  const handleGoBack = () => {
    navigate(-1)
  }

  const handleReload = () => {
    window.location.reload()
  }

  // Determine error type and message
  const getErrorInfo = () => {
    if (error?.status === 404) {
      return {
        title: 'Page Not Found',
        message: 'The page you are looking for does not exist.',
        showHomeButton: true,
      }
    }

    if (error?.status && error.status >= 500) {
      return {
        title: 'Server Error',
        message: 'There was a problem with the server. Please try again later.',
        showHomeButton: true,
      }
    }

    return {
      title: 'Something went wrong',
      message:
        error?.message || error?.statusText || 'An unexpected error occurred.',
      showHomeButton: true,
    }
  }

  const { title, message, showHomeButton } = getErrorInfo()

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 p-4">
      <div className="max-w-md w-full">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-8 text-center">
          <ExclamationTriangleIcon className="mx-auto h-12 w-12 text-red-500 mb-4" />

          <h1 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-2">
            {title}
          </h1>

          <p className="text-gray-600 dark:text-gray-400 mb-6">{message}</p>

          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            {showHomeButton && (
              <Button
                color="primary"
                variant="solid"
                onPress={handleGoHome}
                startContent={<HomeIcon className="h-4 w-4" />}
              >
                Go Home
              </Button>
            )}

            <Button color="default" variant="bordered" onPress={handleGoBack}>
              Go Back
            </Button>

            <Button
              color="default"
              variant="bordered"
              onPress={handleReload}
              startContent={<ArrowPathIcon className="h-4 w-4" />}
            >
              Reload
            </Button>
          </div>

          {showTechnicalDetails && error && (
            <details className="mt-6 text-left">
              <summary className="cursor-pointer text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Technical Details
              </summary>
              <div className="bg-gray-100 dark:bg-gray-700 rounded p-3 text-xs font-mono">
                {error.status && (
                  <div className="mb-2">
                    <strong>Status:</strong> {error.status} {error.statusText}
                  </div>
                )}
                {error.message && (
                  <div className="mb-2">
                    <strong>Message:</strong> {error.message}
                  </div>
                )}
                {error.data != null && (
                  <div>
                    <strong>Data:</strong>{' '}
                    {typeof error.data === 'string'
                      ? error.data
                      : JSON.stringify(error.data, null, 2)}
                  </div>
                )}
              </div>
            </details>
          )}
        </div>
      </div>
    </div>
  )
}
